import { useEditCourseGoBack } from "@/hooks/ui/useEditCourseRoute";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { courseBaseByIdQueryOptions } from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds } from "@vixi/router";
import {
  Button,
  ManageAccessForm,
  ManageAccessFormValues,
  manageAccessValidationSchema,
  PageMainContent,
} from "@vixi/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const routeId = "/_app/courses/$courseId/manage-access" satisfies RouterIds;
const Route = router.routesById[routeId];

export const ManageCourseAccessPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: ({ context: { queryClient }, params: { courseId } }) => {
    return queryClient.ensureQueryData(courseBaseByIdQueryOptions(queryClient, courseId));
  },
};

function RouteComponent() {
  const { t } = useTranslation();

  useEditCourseGoBack();

  // Get Query Data
  const { courseId } = Route.useParams();
  const queryClient = useQueryClient();
  const { data: courseData, isFetching } = useQuery(
    courseBaseByIdQueryOptions(queryClient, courseId),
  );

  const form = useForm<ManageAccessFormValues>({
    disabled: isFetching,
    resolver: zodResolver(manageAccessValidationSchema),
  });
  const {
    formState: { isValid },
  } = form;

  useEffect(() => {
    if (courseData) {
      form.setValue("accessBy", "all");
    }
  }, [courseData]);

  return (
    <PageMainContent ps="medium">
      <h3>{t("CourseDetailsForm.AccessByLabel")}</h3>

      <ManageAccessForm form={form} />

      {isValid && (
        <Button variant="contained" size="large" className="absolute end-26.75 bottom-12.25">
          {t("Action.SaveChanges")}
        </Button>
      )}
    </PageMainContent>
  );
}
